import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, ChevronRight, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface ApprovalItem {
  id: number;
  type: 'leave' | 'wfh';
  requesterName: string;
  requestDate: string;
  detail: string;
}

interface PendingApprovalsData {
  leaveRequests: number;
  wfhRequests: number;
  total: number;
  items: ApprovalItem[];
}

interface PendingApprovalsWidgetProps {
  data?: PendingApprovalsData;
}

export default function PendingApprovalsWidget({ data }: PendingApprovalsWidgetProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const approveMutation = useMutation({
    mutationFn: async ({ id, type }: { id: number; type: 'leave' | 'wfh' }) => {
      const url = type === 'leave'
        ? `/api/leave-requests/${id}/approve`
        : `/api/work-location-records/${id}/approve`;
      return apiRequest(url, { method: "POST" });
    },
    onSuccess: () => {
      toast({ title: "Request approved" });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/summary"] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, type }: { id: number; type: 'leave' | 'wfh' }) => {
      const url = type === 'leave'
        ? `/api/leave-requests/${id}/reject`
        : `/api/work-location-records/${id}/reject`;
      return apiRequest(url, { method: "POST" });
    },
    onSuccess: () => {
      toast({ title: "Request rejected" });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/summary"] });
    },
  });

  const total = data?.total ?? 0;

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 shadow-md">
              <CheckCircle className="h-5 w-5 text-white" />
            </div>
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg font-bold">Approvals</CardTitle>
              {total > 0 && (
                <Badge variant="destructive" className="text-xs px-1.5 py-0 min-w-[20px] justify-center">
                  {total}
                </Badge>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30 gap-1 font-semibold h-8 px-2"
            onClick={() => navigate("/leave-approvals")}
          >
            View All <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        {total === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <CheckCircle className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">All caught up!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data?.items.slice(0, 5).map((item) => (
              <div key={`${item.type}-${item.id}`} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{item.requesterName}</p>
                  <p className="text-xs text-muted-foreground truncate">{item.detail}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                    onClick={() => approveMutation.mutate({ id: item.id, type: item.type })}
                    disabled={approveMutation.isPending}
                    title="Approve"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                    onClick={() => rejectMutation.mutate({ id: item.id, type: item.type })}
                    disabled={rejectMutation.isPending}
                    title="Reject"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {total > 5 && (
              <p className="text-xs text-muted-foreground text-center">+{total - 5} more pending</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
